import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { useQuery } from "@tanstack/react-query";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";
import {
  Heart,
  ImageOff,
  Info,
  List,
  ListPlus,
  MoreVertical,
  Pause,
  Play,
  Shuffle,
} from "lucide-react-native";
import {
  JellyfinItem,
  getItems,
  getMusicCollection,
  imageSource,
  setItemFavorite,
} from "../api/jellyfin";
import { useSession } from "../session/SessionProvider";
import { FocusablePressable } from "../components/FocusablePressable";
import { MatureTopNav, useBottomNavInset } from "../components/MatureTopNav";
import { SimplePosterCard } from "../components/SimplePosterCard";
import { ItemAdminActions } from "../components/ItemAdminActions";
import {
  getMusicArtistHeaderAccent,
  getMusicHeaderAccent,
} from "../utils/imageAccent";
import { colors, systemSansFont } from "../theme/tokens";
import { RootStackParamList } from "../navigation/types";
import { NavigationProp } from "@react-navigation/native";
import { MusicLibrarySidebar } from "./MusicHomeScreen";
import { useMusicPlayback } from "../music/MusicPlaybackProvider";
import { musicPlaylistsOnly } from "../utils/musicPlaylists";
import { useMusicScrollIndicator } from "../components/MusicScrollIndicator";
import { MusicEqualizer } from "../components/MusicEqualizer";

type Props = {
  item: JellyfinItem;
  navigation: NavigationProp<RootStackParamList>;
};

function musicTime(ticks?: number) {
  if (!ticks) return "—";
  const seconds = Math.round(ticks / 10000000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function dimColor(color: string, amount = 0.72) {
  const hex = color.match(/^#([\da-f]{6})$/i)?.[1];
  const channels = hex
    ? [0, 2, 4].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16))
    : color.match(/\d+/g)?.slice(0, 3).map(Number);
  if (!channels || channels.length < 3) return color;
  return `rgb(${channels.map((channel) => Math.round(channel * amount)).join(",")})`;
}

function TrackActions({
  entry,
  credentials,
  visible,
  onQueue,
}: {
  entry: JellyfinItem;
  credentials: NonNullable<ReturnType<typeof useSession>["credentials"]>;
  visible: boolean;
  onQueue: () => void;
}) {
  const [favorite, setFavorite] = useState(Boolean(entry.UserData?.IsFavorite));
  useEffect(() => {
    setFavorite(Boolean(entry.UserData?.IsFavorite));
  }, [entry.Id, entry.UserData?.IsFavorite]);
  return (
    <View style={styles.trackActions}>
      {visible || favorite ? (
        <Pressable
          accessibilityLabel={favorite ? "Remove from favorites" : "Add to favorites"}
          onPress={(event) => {
            event.stopPropagation();
            if (!entry.Id) return;
            const next = !favorite;
            setFavorite(next);
            void setItemFavorite(credentials, entry.Id, next).catch(() =>
              setFavorite(!next),
            );
          }}
          style={styles.trackActionButton}
        >
          <Heart
            color={favorite ? colors.brand : "rgba(255,255,255,0.72)"}
            fill={favorite ? colors.brand : "transparent"}
            size={14}
          />
        </Pressable>
      ) : null}
      {visible ? (
        <Pressable
          accessibilityLabel="Add track to queue"
          onPress={(event) => {
            event.stopPropagation();
            onQueue();
          }}
          style={styles.trackActionButton}
        >
          <MoreVertical color="rgba(255,255,255,0.72)" size={16} />
        </Pressable>
      ) : null}
    </View>
  );
}

export function MusicItemView({ item, navigation }: Props) {
  const { width, height } = useReferenceWindowDimensions();
  const compact = width <= 700;
  const phone = width < 600;
  const [sidebarOverride, setSidebarOverride] = useState<boolean>();
  const sidebarCollapsed = sidebarOverride ?? width <= 950;
  const { credentials, user } = useSession();
  const musicPlayback = useMusicPlayback();
  const bottomNavInset = useBottomNavInset();
  const [followed, setFollowed] = useState(Boolean(item.UserData?.IsFavorite));
  const collectionQuery = useQuery({
    queryKey: ["music-item-collection", item.Id, item.Type],
    queryFn: () => getMusicCollection(credentials!, item),
    enabled: Boolean(credentials && item.Id),
  });
  const libraryAlbums = useQuery({
    queryKey: ["music-item-sidebar", "albums"],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "MusicAlbum",
        SortBy: "DateCreated",
        SortOrder: "Descending",
        Limit: 40,
        Fields: "AlbumArtist,ArtistItems,UserData",
      }),
    enabled: Boolean(credentials),
    staleTime: 60_000,
  });
  const libraryPlaylists = useQuery({
    queryKey: ["music-item-sidebar", "playlists"],
    queryFn: async () =>
      musicPlaylistsOnly(await getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "Playlist",
        SortBy: "SortName",
        SortOrder: "Ascending",
        Fields: "MediaType,UserData",
      })),
    enabled: Boolean(credentials),
    staleTime: 60_000,
  });
  const libraryArtists = useQuery({
    queryKey: ["music-item-sidebar", "artists"],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "MusicArtist",
        SortBy: "SortName",
        SortOrder: "Ascending",
        Limit: 40,
        Fields: "UserData",
      }),
    enabled: Boolean(credentials),
    staleTime: 60_000,
  });
  const isArtist = item.Type === "MusicArtist";
  const [accent, setAccent] = useState(isArtist ? "#858585" : "#505050");
  const [coverFailed, setCoverFailed] = useState(false);
  const [simplified, setSimplified] = useState(false);
  const artistTracksQuery = useQuery({
    queryKey: ["music-artist-top-tracks", item.Id],
    queryFn: () =>
      getItems(credentials!, {
        ArtistIds: item.Id ?? "",
        Recursive: true,
        IncludeItemTypes: "Audio",
        SortBy: "PlayCount,SortName",
        SortOrder: "Descending,Ascending",
        Limit: 40,
        Fields: "Overview,MediaStreams,Album,AlbumId,ArtistItems,UserData",
        EnableUserData: true,
      }),
    enabled: Boolean(credentials && isArtist && item.Id),
  });
  useEffect(() => {
    let active = true;
    if (credentials)
      void (
        isArtist
          ? getMusicArtistHeaderAccent(credentials, item)
          : getMusicHeaderAccent(credentials, item)
      )
        .then((value) => {
          if (active) setAccent(value);
        })
        .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [credentials, isArtist, item]);
  const entries = collectionQuery.data ?? [];
  const musicScrollbar = useMusicScrollIndicator(
    isArtist ? 0 : 360 + 104 + 29 + entries.length * 48 + 80,
  );
  const typeLabel = isArtist
    ? "Artist"
    : item.Type === "Playlist"
      ? "Playlist"
      : "Album";
  const artist = item.AlbumArtist ?? item.ArtistItems?.[0]?.Name;
  const artistId = item.ArtistItems?.[0]?.Id;
  const descriptor =
    item.Overview?.trim() ||
    [
      artist ? `by ${artist}` : null,
      ...(item.Genres ?? []).slice(0, 2),
      item.Studios?.[0]?.Name,
    ]
      .filter(Boolean)
      .join(" · ");
  const isPlaylist = item.Type === "Playlist";
  const bodyAccent = dimColor(accent, 0.7);
  const releaseYear = item.PremiereDate
    ? new Date(item.PremiereDate).getFullYear()
    : item.ProductionYear;
  const artistTracks = useMemo(() => {
    const seen = new Set<string>();
    return (artistTracksQuery.data ?? [])
      .filter((track) => {
        const key = (track.Name ?? track.Id ?? "").trim().toLocaleLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 10);
  }, [artistTracksQuery.data]);
  if (!credentials) return null;
  return (
    <View style={styles.safe}>
      <View style={[styles.shell, compact && styles.shellCompact]}>
        {!phone ? (
          <MusicLibrarySidebar
            playlists={libraryPlaylists.data ?? []}
            albums={libraryAlbums.data ?? []}
            artists={libraryArtists.data ?? []}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarOverride((value) => !(value ?? width <= 950))}
            activeItemId={item.Id}
            onOpen={(entry) =>
              entry.Id && navigation.navigate("Item", { itemId: entry.Id })
            }
            onDestination={(view) => navigation.navigate("Music", { view })}
          />
        ) : null}
        <View style={[styles.mainPanel, !isArtist && { backgroundColor: bodyAccent }]}>
        {isArtist ? (
          <FlatList
            {...musicScrollbar.listProps}
            style={styles.mainList}
            data={artistTracks.slice(0, 5)}
            keyExtractor={(entry, index) => entry.Id ?? `artist-track-${index}`}
            ListHeaderComponent={
              <>
                <View
                  style={[
                    styles.artistHeader,
                    compact && styles.artistHeaderCompact,
                    {
                      height: compact
                        ? 340
                        : Math.max(340, Math.round(height * 0.4)),
                      backgroundColor: accent,
                    },
                  ]}
                >
                  <Svg
                    pointerEvents="none"
                    width={width}
                    height={
                      compact ? 340 : Math.max(340, Math.round(height * 0.4))
                    }
                    style={styles.headerShade}
                  >
                    <Defs>
                      <LinearGradient
                        id="artist-header"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <Stop offset="0" stopColor="#000" stopOpacity="0" />
                        <Stop offset="1" stopColor="#000" stopOpacity="0.5" />
                      </LinearGradient>
                    </Defs>
                    <Rect
                      width={width}
                      height={
                        compact ? 340 : Math.max(340, Math.round(height * 0.4))
                      }
                      fill="url(#artist-header)"
                    />
                  </Svg>
                  <View
                    style={[
                      styles.artistHeaderCopy,
                      compact && styles.artistHeaderCopyCompact,
                    ]}
                  >
                    <Text style={styles.typeLabel}>Artist</Text>
                    <Text
                      numberOfLines={2}
                      style={[
                        styles.artistPageTitle,
                        compact && styles.artistPageTitleCompact,
                      ]}
                    >
                      {item.Name ?? "Untitled"}
                    </Text>
                  </View>
                </View>
                <View style={styles.artistTransition}>
                  <Svg pointerEvents="none" width={width} height={50}>
                    <Defs>
                      <LinearGradient
                        id="artist-transition"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <Stop offset="0" stopColor={accent} />
                        <Stop offset="1" stopColor="#121212" />
                      </LinearGradient>
                    </Defs>
                    <Rect
                      width={width}
                      height={50}
                      fill="url(#artist-transition)"
                    />
                  </Svg>
                </View>
                <View
                  style={[
                    styles.artistBody,
                    compact && styles.artistBodyCompact,
                  ]}
                >
                  <View style={styles.artistControls}>
                    <FocusablePressable
                      accessibilityLabel={`Play ${item.Name ?? "artist"}`}
                      onPress={() =>
                        musicPlayback.loadQueue(artistTracks, 0, true)
                      }
                      style={(focused, pressed, hovered) => [
                        styles.roundPlay,
                        (focused || hovered) && styles.roundPlayActive,
                        pressed && styles.pressed,
                      ]}
                    >
                      <Play color="#fff" fill="#000" size={24} />
                    </FocusablePressable>
                    <FocusablePressable
                      accessibilityLabel={`Follow ${item.Name ?? "artist"}`}
                      onPress={() => {
                        const next = !followed;
                        setFollowed(next);
                        if (credentials && item.Id)
                          void setItemFavorite(credentials, item.Id, next).catch(
                            () => setFollowed(!next),
                          );
                      }}
                      style={(focused, pressed, hovered) => [
                        styles.followButton,
                        (focused || hovered) && styles.pillButtonActive,
                        pressed && styles.pressed,
                      ]}
                    >
                      <Heart
                        color={colors.text}
                        fill={followed ? colors.text : "transparent"}
                        size={18}
                      />
                      <Text style={styles.pillText}>
                        {followed ? "Following" : "Follow"}
                      </Text>
                    </FocusablePressable>
                  </View>
                  <Text style={styles.artistSectionTitle}>Popular</Text>
                </View>
              </>
            }
            renderItem={({ item: entry, index }) => (
              <FocusablePressable
                accessibilityLabel={`Play ${entry.Name ?? `track ${index + 1}`}`}
                onPress={() =>
                  musicPlayback.loadQueue(artistTracks, index, true)
                }
                style={(focused, pressed, hovered) => [
                  styles.artistTrack,
                  compact && styles.artistTrackCompact,
                  (focused || hovered) && styles.trackActive,
                  pressed && styles.pressed,
                ]}
              >
                {(focused, hovered) => (
                  <>
                    <View style={styles.artistTrackIndex}>
                      {musicPlayback.currentTrack?.Id === entry.Id && musicPlayback.playing && !(focused || hovered) ? (
                        <MusicEqualizer />
                      ) : focused || hovered ? (
                        musicPlayback.currentTrack?.Id === entry.Id && musicPlayback.playing ? (
                          <Pause color="#fff" fill="#fff" size={14} />
                        ) : (
                          <Play color="#fff" fill="#fff" size={14} />
                        )
                      ) : (
                        <Text style={styles.trackIndexText}>{index + 1}</Text>
                      )}
                    </View>
                <Image
                  source={imageSource(credentials, entry, "Primary", 80)}
                  resizeMode="cover"
                  style={styles.artistTrackArt}
                />
                <View style={styles.trackCopy}>
                  <Text numberOfLines={1} style={styles.trackTitle}>
                    {entry.Name ?? "Untitled"}
                  </Text>
                  <FocusablePressable
                    accessibilityLabel={`Open album ${entry.Album ?? "album"}`}
                    onPress={(event) => {
                      event.stopPropagation?.();
                      if (entry.AlbumId) navigation.navigate("Item", { itemId: entry.AlbumId });
                    }}
                    style={(_albumFocused, pressed) => [styles.inlineMusicLink, pressed && styles.pressed]}
                  >
                    {(albumFocused, hovered) => <Text numberOfLines={1} style={[styles.trackArtist, (albumFocused || hovered) && styles.inlineMusicLinkTextActive]}>{entry.Album ?? "Unknown album"}</Text>}
                  </FocusablePressable>
                </View>
                <Text style={styles.trackTime}>
                  {musicTime(entry.RunTimeTicks)}
                </Text>
                  </>
                )}
              </FocusablePressable>
            )}
            ListEmptyComponent={
              artistTracksQuery.isLoading ? (
                <ActivityIndicator color={colors.brand} style={styles.loader} />
              ) : (
                <Text style={styles.empty}>No popular tracks found.</Text>
              )
            }
            ListFooterComponent={
              entries.length ? (
                <View style={styles.discography}>
                  <View style={styles.discographyHeading}>
                    <Text style={styles.artistSectionTitle}>Discography</Text>
                    <FocusablePressable
                      accessibilityLabel="See all discography"
                      onPress={() =>
                        item.Id &&
                        navigation.navigate("Music", {
                          view: "discography",
                          artistId: item.Id,
                        })
                      }
                      style={(focused, pressed, hovered) => [
                        (focused || hovered) && styles.discographySeeAllActive,
                        pressed && styles.pressed,
                      ]}
                    >
                      <Text style={styles.discographySeeAll}>See all</Text>
                    </FocusablePressable>
                  </View>
                  <FlatList
                    horizontal
                    data={entries}
                    keyExtractor={(album, albumIndex) =>
                      album.Id ?? `album-${albumIndex}`
                    }
                    contentContainerStyle={styles.albumRow}
                    showsHorizontalScrollIndicator={false}
                    renderItem={({ item: album }) => (
                      <SimplePosterCard
                        item={album}
                        credentials={credentials}
                        width={compact ? 140 : 180}
                        height={compact ? 140 : 180}
                        onPress={() =>
                          album.Id &&
                          navigation.navigate("Item", { itemId: album.Id })
                        }
                      />
                    )}
                  />
                </View>
              ) : null
            }
            contentContainerStyle={[styles.content, bottomNavInset ? { paddingBottom: bottomNavInset } : undefined]}
          />
        ) : (
          <FlatList
            {...musicScrollbar.listProps}
            style={styles.mainList}
            data={entries}
            keyExtractor={(entry, index) => entry.Id ?? `track-${index}`}
            ListHeaderComponent={
              <>
                <View
                  style={[
                    styles.header,
                    compact && styles.headerCompact,
                    compact && isPlaylist && styles.headerCompactPlaylist,
                    { backgroundColor: "#121212" },
                  ]}
                >
                  <Svg
                    pointerEvents="none"
                    width={width}
                    height={compact ? (isPlaylist ? 542 : 571) : 360}
                    style={styles.headerShade}
                  >
                    <Defs>
                      <LinearGradient
                        id="music-header"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <Stop offset="0" stopColor="#121212" />
                        <Stop offset="1" stopColor={accent} />
                      </LinearGradient>
                    </Defs>
                    <Rect
                      width={width}
                      height={compact ? (isPlaylist ? 542 : 571) : 360}
                      fill="url(#music-header)"
                    />
                  </Svg>
                  <View style={[styles.cover, compact && styles.coverCompact]}>
                    {item.ImageTags?.Primary && !coverFailed ? (
                      <Image
                        source={imageSource(credentials, item, "Primary", 464)}
                        onError={() => setCoverFailed(true)}
                        resizeMode="cover"
                        style={[
                          styles.coverImage,
                          compact && styles.coverImageCompact,
                        ]}
                      />
                    ) : (
                      <ImageOff color="rgba(255,255,255,0.24)" size={48} />
                    )}
                  </View>
                  <View
                    style={[
                      styles.headerCopy,
                      !compact && styles.headerCopyWide,
                    ]}
                  >
                    <Text
                      style={[
                        styles.typeLabel,
                        compact && styles.typeLabelCompact,
                      ]}
                    >
                      {typeLabel}
                    </Text>
                    <Text
                      numberOfLines={compact ? 2 : 1}
                      style={[
                        styles.musicTitle,
                        compact && styles.musicTitleCompact,
                      ]}
                    >
                      {item.Name ?? "Untitled"}
                    </Text>
                    {descriptor ? (
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.descriptor,
                          compact && styles.descriptorCompact,
                        ]}
                      >
                        {descriptor}
                      </Text>
                    ) : null}
                    <View
                      style={[
                        styles.creatorRow,
                        compact && styles.creatorRowCompact,
                      ]}
                    >
                      {artist ? (
                        <>
                          <View style={styles.creatorAvatar}>
                            {artistId ? (
                              <Image
                                source={imageSource(
                                  credentials,
                                  { Id: artistId },
                                  "Primary",
                                  48,
                                )}
                                resizeMode="cover"
                                style={styles.creatorImage}
                              />
                            ) : (
                              <Text style={styles.creatorInitial}>
                                {artist.slice(0, 1)}
                              </Text>
                            )}
                          </View>
                          <FocusablePressable
                            accessibilityLabel={`Open artist ${artist}`}
                            onPress={() => {
                              if (artistId) navigation.navigate("Item", { itemId: artistId });
                            }}
                            style={(_focused, pressed) => [styles.creatorArtistLink, pressed && styles.pressed]}
                          >
                            {(artistFocused, hovered) => (
                              <Text style={[styles.artist, (artistFocused || hovered) && styles.inlineMusicLinkTextActive]}>
                                {artist}
                              </Text>
                            )}
                          </FocusablePressable>
                        </>
                      ) : null}
                      <Text style={styles.summary}>
                        {artist ? "· " : "• "}
                        {[
                          releaseYear,
                          entries.length ? `${entries.length} songs` : null,
                          !isPlaylist && item.RunTimeTicks
                            ? musicTime(item.RunTimeTicks)
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" • ")}
                      </Text>
                    </View>
                  </View>
                </View>
                <Svg
                  pointerEvents="none"
                  width={width}
                  height={180}
                  style={[styles.albumBodyHighlight, { top: compact ? (isPlaylist ? 542 : 571) : 360 }]}
                >
                  <Defs>
                    <LinearGradient id="album-body-highlight" x1="0" y1="0" x2="0" y2="1">
                      <Stop offset="0" stopColor={accent} />
                      <Stop offset="1" stopColor={bodyAccent} />
                    </LinearGradient>
                  </Defs>
                  <Rect width={width} height={180} fill="url(#album-body-highlight)" />
                </Svg>
                <View
                  style={[
                    styles.controls,
                    compact ? styles.controlsCompact : styles.controlsWide,
                  ]}
                >
                  <FocusablePressable
                    accessibilityLabel={`Play ${item.Name ?? typeLabel}`}
                    onPress={() =>
                      musicPlayback.loadQueue(entries, 0, true)
                    }
                    style={(focused, pressed, hovered) => [
                      styles.roundPlay,
                      !compact && styles.roundPlayWide,
                      (focused || hovered) && styles.roundPlayActive,
                      pressed && styles.pressed,
                    ]}
                  >
                    <Play color="#fff" fill="#000" size={24} />
                  </FocusablePressable>
                  {user?.Policy?.IsAdministrator ? (
                    <ItemAdminActions
                      accent={accent}
                      compact={compact}
                      credentials={credentials}
                      dense
                      item={item}
                      onDeleted={() => navigation.goBack()}
                      onUpdated={() => void collectionQuery.refetch()}
                    />
                  ) : null}
                  <FocusablePressable
                    accessibilityLabel="Play mix"
                    onPress={() => musicPlayback.playShuffled(entries)}
                    style={(focused, pressed, hovered) => [
                      styles.pillButton,
                      compact && styles.pillButtonCompact,
                      (focused || hovered) && styles.pillButtonActive,
                      pressed && styles.pressed,
                    ]}
                  >
                    <Shuffle color={colors.text} size={17} />
                    <Text style={styles.pillText}>Mix</Text>
                  </FocusablePressable>
                  <FocusablePressable
                    accessibilityLabel="Add to queue"
                    onPress={() => musicPlayback.addToQueue(entries)}
                    style={(focused, pressed, hovered) => [
                      styles.pillButton,
                      compact && styles.pillButtonCompact,
                      (focused || hovered) && styles.pillButtonActive,
                      pressed && styles.pressed,
                    ]}
                  >
                    <ListPlus color={colors.text} size={17} />
                    <Text style={styles.pillText}>Queue</Text>
                  </FocusablePressable>
                  <FocusablePressable
                    accessibilityLabel={simplified ? "Use list view" : "Use compact view"}
                    onPress={() => setSimplified((value) => !value)}
                    style={(focused, pressed, hovered) => [styles.modeLabel, (focused || hovered) && styles.modeLabelActive, pressed && styles.pressed]}
                  >
                    <List color="rgba(255,255,255,0.72)" size={16} />
                    <Text style={styles.modeText}>
                      {simplified ? "Compact" : "List"}
                    </Text>
                  </FocusablePressable>
                </View>
                <View
                  style={[
                    styles.trackHeader,
                    compact && styles.trackHeaderCompact,
                  ]}
                >
                    <Text style={styles.trackHeaderIndex}>#</Text>
                    <Text style={styles.trackHeaderTitle}>Title</Text>
                    {isPlaylist && !compact && !simplified ? (
                      <Text style={styles.trackHeaderAlbum}>Album</Text>
                    ) : null}
                    <View style={styles.trackHeaderInfo}><Info color="rgba(255,255,255,0.70)" size={14} /></View>
                </View>
              </>
            }
            renderItem={({ item: entry, index }) => (
              <FocusablePressable
                accessibilityLabel={`Play ${entry.Name ?? `track ${index + 1}`}`}
                onPress={() =>
                  musicPlayback.loadQueue(entries, index, true)
                }
                style={(focused, pressed, hovered) => [
                  styles.track,
                  simplified && styles.trackSimplified,
                  (focused || hovered) && styles.trackActive,
                  pressed && styles.pressed,
                ]}
              >
                {(focused, hovered) => (
                  <>
                <View style={styles.trackIndex}>
                  {musicPlayback.currentTrack?.Id === entry.Id && musicPlayback.playing && !(focused || hovered) ? (
                    <MusicEqualizer />
                  ) : focused || hovered ? (
                    musicPlayback.currentTrack?.Id === entry.Id && musicPlayback.playing ? (
                      <Pause color="#fff" fill="#fff" size={14} />
                    ) : (
                      <Play color="#fff" fill="#fff" size={14} />
                    )
                  ) : (
                    <Text style={styles.trackIndexText}>{index + 1}</Text>
                  )}
                </View>
                {!compact && isPlaylist && !simplified ? (
                  <Image
                    source={imageSource(credentials, entry, "Primary", 80)}
                    resizeMode="cover"
                    style={styles.playlistTrackArt}
                  />
                ) : null}
                <View style={styles.trackCopy}>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.trackTitle,
                      musicPlayback.currentTrack?.Id === entry.Id &&
                        styles.trackTitleCurrent,
                    ]}
                  >
                    {entry.Name ?? "Untitled"}
                  </Text>
                  {!simplified ? (
                    <View style={styles.trackArtists}>
                      {entry.ArtistItems?.length ? entry.ArtistItems.map((entryArtist, artistIndex) => (
                        <React.Fragment key={entryArtist.Id ?? `${entryArtist.Name}-${artistIndex}`}>
                          {artistIndex ? <Text style={styles.trackArtistSeparator}>, </Text> : null}
                          <FocusablePressable
                            accessibilityLabel={`Open artist ${entryArtist.Name ?? "artist"}`}
                            onPress={(event) => {
                              event.stopPropagation?.();
                              if (entryArtist.Id) navigation.navigate("Item", { itemId: entryArtist.Id });
                            }}
                            style={(_focused, pressed) => [styles.trackArtistLink, pressed && styles.pressed]}
                          >
                            {(artistFocused, hovered) => <Text numberOfLines={1} style={[styles.trackArtist, (artistFocused || hovered) && styles.inlineMusicLinkTextActive]}>{entryArtist.Name ?? "Unknown artist"}</Text>}
                          </FocusablePressable>
                        </React.Fragment>
                      )) : <Text numberOfLines={1} style={styles.trackArtist}>{artist || "Unknown artist"}</Text>}
                    </View>
                  ) : null}
                </View>
                {!compact && isPlaylist && !simplified ? (
                  <FocusablePressable
                    accessibilityLabel={`Open album ${entry.Album ?? item.Name ?? "album"}`}
                    onPress={(event) => {
                      event.stopPropagation?.();
                      if (entry.AlbumId) navigation.navigate("Item", { itemId: entry.AlbumId });
                    }}
                    style={(_albumFocused, pressed) => [styles.trackAlbum, pressed && styles.pressed]}
                  >
                    {(albumFocused, hovered) => <Text numberOfLines={1} style={[styles.trackAlbumText, (albumFocused || hovered) && styles.inlineMusicLinkTextActive]}>{entry.Album ?? item.Name}</Text>}
                  </FocusablePressable>
                ) : null}
                <Text style={styles.trackTime}>
                  {musicTime(entry.RunTimeTicks)}
                </Text>
                <TrackActions
                  entry={entry}
                  credentials={credentials}
                  visible={focused || hovered}
                  onQueue={() => musicPlayback.addToQueue([entry])}
                />
                  </>
                )}
              </FocusablePressable>
            )}
            ListEmptyComponent={
              collectionQuery.isLoading ? (
                <ActivityIndicator color={accent} style={styles.loader} />
              ) : (
                <Text style={styles.empty}>No music found.</Text>
              )
            }
            contentContainerStyle={[styles.content, bottomNavInset ? { paddingBottom: bottomNavInset } : undefined]}
          />
        )}
        {musicScrollbar.indicator}
        </View>
      </View>
      <MatureTopNav navigation={navigation} active="Music" overlay />
    </View>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: "#000" },
  shell: {
    flex: 1,
    paddingTop: 80,
    paddingHorizontal: 8,
    paddingBottom: 8,
    flexDirection: "row",
    gap: 8,
  },
  shellCompact: { paddingTop: 124, paddingHorizontal: 4, paddingBottom: 0 },
  mainPanel: {
    flex: 1,
    overflow: "hidden",
    borderRadius: 8,
    backgroundColor: "#121212",
  },
  mainList: { flex: 1 },
  content: { paddingBottom: 80 },
  header: {
    height: 360,
    padding: 30,
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 24,
    overflow: "hidden",
  },
  headerCompact: {
    height: 571,
    paddingTop: 80,
    paddingHorizontal: 16,
    paddingBottom: 24,
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 24,
  },
  headerCompactPlaylist: { height: 542 },
  headerShade: { position: "absolute", top: 0, left: 0 },
  albumBodyHighlight: { position: "absolute", left: 0 },
  cover: {
    width: 300,
    height: 300,
    overflow: "visible",
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#282828",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 30,
    elevation: 18,
  },
  coverCompact: { width: 300, height: 300, alignSelf: "center" },
  coverImage: { width: 300, height: 300, borderRadius: 4 },
  coverImageCompact: { width: 300, height: 300 },
  headerCopy: { flex: 1, minWidth: 0, justifyContent: "flex-end" },
  headerCopyWide: { transform: [{ translateY: -9 }] },
  typeLabel: {
    // RN Web's Text line box is tighter than the mature h1/span pairing.
    // This produces the same rendered 47dp label-to-title rhythm.
    marginBottom: 21,
    color: colors.text,
    fontSize: 14,
    fontWeight: "700",
    fontFamily: systemSansFont,
  },
  typeLabelCompact: { marginBottom: 15 },
  musicTitle: {
    color: colors.text,
    fontSize: 64,
    fontWeight: "600",
    fontFamily: systemSansFont,
  },
  artistTitle: { fontSize: 82, lineHeight: 88 },
  musicTitleCompact: { fontSize: 28, lineHeight: 34 },
  descriptor: {
    // Match the mature h1-to-description glyph spacing after RN Web line-boxing.
    marginTop: 19,
    color: "rgba(255,255,255,0.60)",
    fontSize: 13,
    fontFamily: systemSansFont,
  },
  descriptorCompact: { marginTop: 21 },
  creatorRow: { marginTop: 20, flexDirection: "row", alignItems: "center" },
  creatorRowCompact: { marginTop: 26 },
  creatorAvatar: {
    width: 24,
    height: 24,
    marginRight: 8,
    borderRadius: 12,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ddd",
  },
  creatorImage: { width: 24, height: 24 },
  creatorInitial: { color: "#222", fontSize: 11, fontWeight: "800" },
  creatorArtistLink: {
    height: 24,
    justifyContent: "center",
    borderRadius: 2,
  },
  artist: {
    color: colors.text,
    fontSize: 14,
    fontWeight: "600",
    fontFamily: systemSansFont,
  },
  summary: {
    marginLeft: 2,
    color: "rgba(255,255,255,0.72)",
    fontSize: 14,
    fontFamily: systemSansFont,
  },
  controls: {
    height: 104,
    paddingHorizontal: 30,
    flexDirection: "row",
    alignItems: "center",
    gap: 18,
  },
  controlsWide: { paddingTop: 12, gap: 24 },
  controlsCompact: { paddingHorizontal: 12, gap: 12 },
  roundPlay: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.brand,
  },
  roundPlayWide: { marginRight: 34 },
  roundPlayActive: { transform: [{ scale: 1.06 }] },
  pillButton: {
    height: 38,
    paddingHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.36)",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  pillButtonCompact: { paddingHorizontal: 12, gap: 6 },
  pillButtonActive: {
    borderColor: colors.text,
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  pillText: { color: colors.text, fontFamily: systemSansFont, fontSize: 13, fontWeight: "600" },
  modeLabel: {
    marginLeft: "auto",
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
  },
  modeText: { color: "rgba(255,255,255,0.82)", fontFamily: systemSansFont, fontSize: 13 },
  modeLabelActive: { opacity: 1 },
  trackHeader: {
    height: 29,
    marginHorizontal: 30,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.10)",
    flexDirection: "row",
    alignItems: "flex-start",
  },
  trackHeaderCompact: { marginHorizontal: 12 },
  trackHeaderIndex: {
    width: 40,
    color: "rgba(255,255,255,0.62)",
    textAlign: "center",
    fontSize: 12,
    fontFamily: systemSansFont,
  },
  trackHeaderTitle: { flex: 8, color: "rgba(255,255,255,0.70)", fontFamily: systemSansFont, fontSize: 12 },
  trackHeaderAlbum: {
    flex: 5,
    color: "rgba(255,255,255,0.70)",
    fontSize: 12,
    fontFamily: systemSansFont,
  },
  trackHeaderInfo: {
    flex: 1,
    paddingRight: 8,
    alignItems: "flex-end",
  },
  sectionTitle: {
    marginHorizontal: 32,
    marginBottom: 16,
    color: colors.text,
    fontSize: 24,
    lineHeight: 30,
    fontWeight: "800",
  },
  albumRow: { paddingHorizontal: 32, gap: 18 },
  track: {
    minHeight: 56,
    marginHorizontal: 30,
    paddingHorizontal: 16,
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  trackSimplified: { minHeight: 40 },
  trackActive: { backgroundColor: "rgba(255,255,255,0.1)" },
  trackIndex: {
    width: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  trackIndexText: { color: "rgba(255,255,255,0.64)", fontFamily: systemSansFont, fontSize: 16 },
  playlistTrackArt: {
    width: 40,
    height: 40,
    marginRight: 12,
    borderRadius: 4,
    backgroundColor: "#282828",
  },
  trackCopy: { flex: 8, minWidth: 0 },
  trackTitle: { color: colors.text, fontFamily: systemSansFont, fontSize: 16 },
  trackTitleCurrent: { color: colors.brand },
  trackArtist: {
    color: "rgba(255,255,255,0.66)",
    fontSize: 14,
    lineHeight: 17,
    fontFamily: systemSansFont,
  },
  trackArtists: { minWidth: 0, marginTop: 7, flexDirection: "row", alignItems: "center", overflow: "hidden" },
  trackArtistLink: { flexShrink: 1, minWidth: 0, borderRadius: 2 },
  trackArtistSeparator: { color: "rgba(255,255,255,0.66)", fontFamily: systemSansFont, fontSize: 14, lineHeight: 17 },
  trackAlbum: { flex: 5, minWidth: 0, borderRadius: 2 },
  trackAlbumText: { color: "rgba(255,255,255,0.66)", fontFamily: systemSansFont, fontSize: 14 },
  inlineMusicLink: { alignSelf: "flex-start", flexShrink: 1, minWidth: 0, borderRadius: 2 },
  inlineMusicLinkTextActive: { color: "#fff", textDecorationLine: "underline" },
  trackTime: {
    flex: 1,
    minWidth: 48,
    paddingRight: 8,
    color: "rgba(255,255,255,0.66)",
    fontSize: 14,
    fontFamily: systemSansFont,
    textAlign: "right",
  },
  trackActions: {
    width: 56,
    marginLeft: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
  },
  trackActionButton: {
    width: 24,
    height: 28,
    borderRadius: 3,
    alignItems: "center",
    justifyContent: "center",
  },
  artistHeader: {
    position: "relative",
    overflow: "hidden",
    justifyContent: "flex-end",
  },
  artistHeaderCompact: { minHeight: 280 },
  artistBackdrop: { ...StyleSheet.absoluteFillObject, opacity: 0.62 },
  artistHeaderCopy: { paddingHorizontal: 32, paddingBottom: 24 },
  artistHeaderCopyCompact: { paddingHorizontal: 16, paddingBottom: 18 },
  artistPageTitle: {
    color: colors.text,
    fontSize: 96,
    lineHeight: 106,
    fontWeight: "900",
  },
  artistPageTitleCompact: { fontSize: 32, lineHeight: 38 },
  artistTransition: { height: 50 },
  artistBody: { paddingHorizontal: 32 },
  artistBodyCompact: { paddingHorizontal: 16 },
  artistControls: {
    paddingTop: 24,
    paddingBottom: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 24,
  },
  followButton: {
    height: 40,
    paddingHorizontal: 20,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.32)",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  artistSectionTitle: {
    marginTop: 4,
    marginBottom: 16,
    color: colors.text,
    fontSize: 24,
    lineHeight: 30,
    fontWeight: "800",
  },
  artistTrack: {
    height: 56,
    marginHorizontal: 32,
    paddingRight: 16,
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  artistTrackCompact: { marginHorizontal: 16, paddingRight: 8 },
  artistTrackIndex: {
    width: 40,
    color: "#b3b3b3",
    textAlign: "center",
    fontSize: 16,
  },
  artistTrackArt: {
    width: 40,
    height: 40,
    marginRight: 12,
    borderRadius: 4,
    backgroundColor: "#282828",
  },
  discography: { marginTop: 38, marginBottom: 20 },
  discographyHeading: {
    marginHorizontal: 32,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  discographySeeAll: {
    color: "#b3b3b3",
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
  },
  discographySeeAllActive: { opacity: 0.78 },
  loader: { marginTop: 44 },
  empty: { marginHorizontal: 32, color: "rgba(255,255,255,0.6)", fontSize: 15 },
  pressed: { opacity: 0.76 },
});
